import * as React from "react"
import { useQuery } from "@tanstack/react-query"

import { DataState } from "@/components/DataState"
import { WorkPanel } from "@/components/WorkPanel"
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table"
import { useAuth } from "@/lib/auth"
import { formatAmount, formatDate } from "@/lib/format"
import { cn } from "@/lib/utils"

import { useBooksFilters } from "@/routes/books/context"
import { fetchCashFlow, type CashFlowActivity } from "@/routes/statements/api"
import { EmptyBook, StatementCheck } from "@/routes/statements/StatementChecks"
import { BotonDescargar } from "@/components/BotonDescargar"
import { bookToApi } from "@/routes/books/mapping"
import { t } from "@/lib/i18n"

/**
 * Estado de flujos de efectivo, metodo DIRECTO.
 *
 * No es el flujo de caja de Finanzas: aquel proyecta desde el modelo, este
 * explica el efectivo que ya se movio. Sale de los asientos que tocan caja y
 * banco, y la comprobacion de arriba no es decorativa — si el flujo no explica
 * exactamente la variacion del saldo, no sirve para nada.
 */
const ACTIVIDADES: { clave: CashFlowActivity; etiqueta: string; nota: string }[] = [
  {
    clave: "operacion",
    etiqueta: "Actividades de operacion",
    nota: "Lo que produce el resultado y el capital de trabajo que lo acompana.",
  },
  {
    clave: "inversion",
    etiqueta: "Actividades de inversion",
    nota: "Lo que cambia el activo de largo plazo.",
  },
  {
    clave: "financiamiento",
    etiqueta: "Actividades de financiamiento",
    nota: "Lo que cambia la deuda financiera y el patrimonio.",
  },
  {
    clave: "sin_clasificar",
    etiqueta: "Sin clasificar",
    nota: "Movimientos de efectivo cuya contrapartida no tiene cuenta de grupo. No se reparten entre las tres de arriba: tres subtotales creibles y falsos son peores que uno vacio.",
  },
]

export function CashFlowStatementPage() {
  const { book, from, to } = useBooksFilters()
  const { activeTenantId, activeEntityId } = useAuth()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["cash-flow-statement", activeTenantId, book, from, to],
    queryFn: () => fetchCashFlow({ book, from, to }),
  })

  return (
    <div className="space-y-3">
      {data ? (
        <>
          <StatementCheck
            cuadra={data.comprobacion.cuadra}
            diferencia={data.comprobacion.diferencia}
            etiqueta={
              data.comprobacion.cuadra
                ? "El flujo explica la variacion del efectivo"
                : "El flujo NO explica la variacion del efectivo"
            }
            detalle={`${formatAmount(data.efectivo_inicial)} + ${formatAmount(
              data.variacion,
            )} = ${formatAmount(data.efectivo_final)}`}
          />
          <EmptyBook asientos={data.asientos} />
        </>
      ) : null}

      <WorkPanel
        actions={
          <BotonDescargar
            recurso="flujo-efectivo"
            filtros={{ entity_id: activeEntityId, libro: bookToApi(book), desde: from, hasta: to }}
          />
        }
        title={t("Flujo de efectivo")}
        meta={
          data ? (
            <span className="text-2xs text-muted-foreground">
              metodo <span className="font-medium">{data.metodo}</span> ·{" "}
              <span className="code">{formatDate(data.desde)}</span> a{" "}
              <span className="code">{formatDate(data.hasta)}</span>
            </span>
          ) : null
        }
        footnote={t("Metodo directo: cada asiento que toca caja o banco dice a donde fue esa plata, en su contrapartida. No se estima nada. Un traspaso entre dos cuentas propias no aparece, porque mover plata de un bolsillo a otro no es un flujo.")}
      >
        <DataState
          loading={isLoading}
          error={error}
          onRetry={() => void refetch()}
          empty={false}
          columns={[40, 20]}
        >
          {data ? (
            <Table>
              <TableBody>
                <TableRow className="hover:bg-transparent">
                  <TableCell className="text-2xs uppercase tracking-[0.06em] text-muted-foreground">
                    {t("Efectivo al inicio")}</TableCell>
                  <TableCell className="num w-40">
                    {formatAmount(data.efectivo_inicial)}
                  </TableCell>
                </TableRow>

                {ACTIVIDADES.map(({ clave, etiqueta, nota }) => {
                  const actividad = data.actividades[clave]
                  // La actividad vacia se omite SALVO las tres principales: que
                  // "inversion" no aparezca no es lo mismo que que sea cero, y
                  // en un estado de flujos las tres son parte de la forma.
                  if (!actividad || (clave === "sin_clasificar" && !actividad.lineas.length)) {
                    return null
                  }
                  return (
                    <React.Fragment key={clave}>
                      <TableRow className="row-group hover:bg-muted/60">
                        <TableCell className="[&]:py-1">
                          <span className="text-2xs font-semibold uppercase tracking-[0.07em]">
                            {etiqueta}
                          </span>
                          <span className="ml-2 text-2xs font-normal normal-case text-muted-foreground">
                            {nota}
                          </span>
                        </TableCell>
                        <TableCell
                          className={cn(
                            "num w-40 [&]:py-1 font-semibold",
                            actividad.total < 0 && "text-danger",
                            !actividad.total && "num-zero",
                          )}
                        >
                          {actividad.total ? formatAmount(actividad.total) : "—"}
                        </TableCell>
                      </TableRow>

                      {actividad.lineas.map((linea) => (
                        <TableRow key={`${clave}-${linea.codigo ?? "sin"}`}>
                          <TableCell className="pl-6">
                            <span className="code mr-2 text-muted-foreground">
                              {linea.codigo ?? "—"}
                            </span>
                            {linea.nombre}
                          </TableCell>
                          <TableCell
                            className={cn("num w-40", linea.importe < 0 && "text-danger")}
                          >
                            {formatAmount(linea.importe)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </React.Fragment>
                  )
                })}

                <TableRow className="hover:bg-transparent">
                  <TableCell className="rule-double text-2xs font-semibold uppercase tracking-[0.06em]">
                    {t("Variacion neta del efectivo")}</TableCell>
                  <TableCell className="num-strong rule-double w-40">
                    {formatAmount(data.variacion)}
                  </TableCell>
                </TableRow>

                <TableRow className="hover:bg-transparent">
                  <TableCell className="text-2xs uppercase tracking-[0.06em] text-muted-foreground">
                    {t("Efectivo al cierre")}</TableCell>
                  <TableCell className="num-strong w-40">
                    {formatAmount(data.efectivo_final)}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          ) : null}
        </DataState>
      </WorkPanel>
    </div>
  )
}
